// Preview mode catalogue (pure, no React): every previewable output, grouped and ordered, with availability rules,
// the tax-form item ids (party x form x scenario) and the print units used by "Print all".
// Report ids are the Reports tab registry ids (dis/reports/registry.tsx), so rendering stays in one place.
import type { Case, CaseResult, PartyIndex } from '@divorceos/schema';

export type PreviewGroupId = 'court' | 'client' | 'analysis' | 'detail' | 'tax';
export type TaxForm = 'fed' | 'ca';
export type TaxScenario = 'g' | 'p';

export interface PreviewItemDef {
  /** Tab id: a registry report id, or "tax-fed" / "tax-ca". */
  id: string;
  group: PreviewGroupId;
  title: string;
  /** Computed on demand (spinner while it runs). */
  heavy?: boolean;
  /** Follows the case's Monthly / Yearly setting. */
  period?: boolean;
}

export const PREVIEW_GROUPS: { id: PreviewGroupId; title: string }[] = [
  { id: 'court', title: 'Court' },
  { id: 'client', title: 'Client' },
  { id: 'analysis', title: 'Analysis' },
  { id: 'detail', title: 'Detail' },
  { id: 'tax', title: 'Tax forms' },
];

export const PREVIEW_ITEMS: PreviewItemDef[] = [
  { id: 'summary', group: 'court', title: 'Summary', period: true },
  { id: 'bench', group: 'court', title: 'Bench report', period: true },
  { id: 'formal', group: 'court', title: 'Formal report', period: true },
  { id: 'findings', group: 'court', title: 'Findings and rebuttals', period: true },
  { id: 'orders', group: 'court', title: 'Support order' },
  { id: 'income-expenses', group: 'client', title: 'Income and expense worksheet' },
  { id: 'client-summary', group: 'client', title: 'Client financial summary', period: true },
  { id: 'compare', group: 'analysis', title: 'Snapshot comparison', period: true },
  { id: 'variation', group: 'analysis', title: 'Input variation', heavy: true, period: true },
  { id: 'smith', group: 'analysis', title: 'Additional Support Above Base Pay', heavy: true },
  { id: 'releases', group: 'analysis', title: 'Exemption releases', heavy: true, period: true },
  { id: 'data-detail', group: 'detail', title: 'Input detail', period: true },
  { id: 'inputs-impact', group: 'detail', title: 'Inputs by impact', period: true },
  { id: 'settings', group: 'detail', title: 'Settings' },
  { id: 'guideline', group: 'detail', title: 'Guideline summary', period: true },
  { id: 'main', group: 'detail', title: 'Main screen snapshot', period: true },
  { id: 'tax-fed', group: 'tax', title: 'Federal Form 1040 and schedules' },
  { id: 'tax-ca', group: 'tax', title: 'California Form 540' },
];

export const DEFAULT_PREVIEW_ITEM = 'bench';

export const previewItem = (id: string): PreviewItemDef | undefined => PREVIEW_ITEMS.find((i) => i.id === id);
export const itemsOfGroup = (g: PreviewGroupId): PreviewItemDef[] => PREVIEW_ITEMS.filter((i) => i.group === g);
export const groupOf = (id: string): PreviewGroupId => previewItem(id)?.group ?? 'court';

// ------------------------------------------------------------------ availability

/** What availability depends on (kept small so it is easy to test). */
export interface PreviewFacts {
  hasResult: boolean;
  hasProposal: boolean;
  childCount: number;
}

/**
 * A proposal exists when the Proposed panel differs from guideline: a tactic other than 9 (guideline amounts), or
 * dependency releases in the proposal (selected children, or releases found by the engine).
 */
export function proposalExists(c: Pick<Case, 'proposed'>, r: Pick<CaseResult, 'proposed'> | null): boolean {
  if (!r) return false;
  if (c.proposed.tactic !== '9') return true;
  if ((c.proposed.releaseChildren?.length ?? 0) > 0) return true;
  return r.proposed.releases > 0;
}

export function previewFacts(c: Pick<Case, 'proposed' | 'children'>, r: Pick<CaseResult, 'proposed'> | null): PreviewFacts {
  return { hasResult: !!r, hasProposal: proposalExists(c, r), childCount: c.children.length };
}

/** Why an item cannot be shown, or null when it can. */
export function unavailableReason(id: string, f: PreviewFacts): string | null {
  const it = previewItem(id);
  if (!it) return 'Unknown output.';
  if (!f.hasResult) return 'No result yet: the case has an engine error or has not been calculated.';
  if (id === 'releases' && f.childCount === 0) return 'The case has no children, so there are no exemptions to release.';
  return null;
}

/** Scenarios the tax-form items can show: Proposed only when a proposal exists. */
export function taxScenarios(f: PreviewFacts): TaxScenario[] {
  return f.hasProposal ? ['g', 'p'] : ['g'];
}

/** Keep a remembered scenario only while it is still available. */
export function effectiveScenario(want: TaxScenario, f: PreviewFacts): TaxScenario {
  return taxScenarios(f).includes(want) ? want : 'g';
}

// ------------------------------------------------------------------ tax ids and print units

/** Render / print id of one tax form page: "tax:<fed|ca>:<party>:<g|p>" (understood by ReportBody). */
export const taxId = (form: TaxForm, party: PartyIndex, sc: TaxScenario): string => `tax:${form}:${party}:${sc}`;

export function parseTaxId(id: string): { form: TaxForm; party: PartyIndex; scenario: TaxScenario } | null {
  const m = /^tax:(fed|ca):([01]):([gp])$/.exec(id);
  if (!m) return null;
  return { form: m[1] as TaxForm, party: Number(m[2]) as PartyIndex, scenario: m[3] as TaxScenario };
}

export interface TaxState { party: PartyIndex; scenario: TaxScenario }

/** Render id for a tab: the report id itself, or the tax id for the current party and scenario. */
export function renderId(itemId: string, t: TaxState): string {
  if (itemId === 'tax-fed') return taxId('fed', t.party, t.scenario);
  if (itemId === 'tax-ca') return taxId('ca', t.party, t.scenario);
  return itemId;
}

export interface PrintUnit { id: string; group: PreviewGroupId; title: string; available: boolean; reason: string | null }

/**
 * Everything "Print all" can print, in catalogue order. Tax forms expand to one unit per party and form, for the
 * scenario chosen in the preview.
 */
export function printUnits(labels: [string, string], f: PreviewFacts, scenario: TaxScenario): PrintUnit[] {
  const sc = effectiveScenario(scenario, f);
  const out: PrintUnit[] = [];
  for (const it of PREVIEW_ITEMS) {
    const reason = unavailableReason(it.id, f);
    if (it.group !== 'tax') { out.push({ id: it.id, group: it.group, title: it.title, available: !reason, reason }); continue; }
    const form: TaxForm = it.id === 'tax-fed' ? 'fed' : 'ca';
    for (const p of [0, 1] as const) {
      out.push({ id: taxId(form, p, sc), group: 'tax', title: `${it.id === 'tax-fed' ? 'Federal 1040' : 'California 540'} · ${labels[p]} (${sc === 'p' ? 'Proposed' : 'Guideline'})`, available: !reason, reason });
    }
  }
  return out;
}

/**
 * Default "Print all" selection: the reports ticked on the Reports tab. The Reports tab's single "Tax forms" report
 * maps to both forms of the party chosen in the tax form viewer.
 */
export function defaultPrintSelection(checked: Record<string, boolean>, units: PrintUnit[], formParty: PartyIndex): string[] {
  const want = new Set(Object.keys(checked).filter((k) => checked[k]));
  return units.filter((u) => {
    if (!u.available) return false;
    const t = parseTaxId(u.id);
    if (t) return want.has('forms') && t.party === formParty;
    return want.has(u.id);
  }).map((u) => u.id);
}

/** Reports tab report id -> preview tab id. */
export const previewTabFor = (reportId: string): string => (reportId === 'forms' ? 'tax-fed' : previewItem(reportId) ? reportId : DEFAULT_PREVIEW_ITEM);

/** Next / previous tab across the whole catalogue (arrow keys), wrapping. */
export function stepItem(id: string, dir: 1 | -1): string {
  const i = PREVIEW_ITEMS.findIndex((x) => x.id === id);
  const n = PREVIEW_ITEMS.length;
  return PREVIEW_ITEMS[((i < 0 ? 0 : i) + dir + n) % n]?.id ?? DEFAULT_PREVIEW_ITEM;
}
